import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const DARK_DIALOG = "bg-slate-900 border-slate-700 text-slate-100";
export const DARK_INPUT = "bg-slate-950 border-slate-700 text-slate-100 [color-scheme:dark]";

export const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
export const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
export const errMsg = (e, fb = "Something went wrong") => e?.response?.data?.detail || fb;

const TONE = {
  ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  warn: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  bad: "bg-red-500/15 text-red-300 border-red-500/30",
  info: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  mute: "bg-slate-700/40 text-slate-300 border-slate-600",
};
const TONE_OF = {
  successful: "ok", completed: "ok", fully_paid: "ok", processed: "ok", mock_processed: "ok", scheduled_offline: "ok", fully_assigned: "ok",
  pending: "warn", pending_review: "warn", processing: "warn", created: "warn", advance_paid: "warn", partially_refunded: "warn", on_hold: "warn", partially_assigned: "warn",
  passed: "ok", active: "ok", disabled: "bad", signed_verified: "ok", awaiting_signature: "warn", verification_failed: "bad",
  failed: "bad", cancelled: "bad", declined: "bad", refunded: "bad",
  contacted: "info", finding_workers: "info", in_progress: "info", work_started: "info",
  PENDING: "warn", APPROVED: "ok", CARD_PRINTED: "info", ISSUED: "ok", REJECTED: "bad", SUSPENDED: "warn", REVOKED: "bad",
  generated: "warn", printed: "info", issued: "ok", replaced: "mute",
};

export const Pill = ({ value, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-semibold whitespace-nowrap ${TONE[TONE_OF[value] || "mute"]}`}>
    {(value || "—").replace(/_/g, " ")}
  </span>
);

export const FilterSelect = ({ value, onChange, options, placeholder, testid, labels = {} }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger className={`${DARK_INPUT} h-9 w-full md:w-44`} data-testid={testid}><SelectValue placeholder={placeholder}/></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{labels[o] || o.replace(/_/g, " ")}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const DateInput = ({ value, onChange, testid }) => (
  <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} className={`${DARK_INPUT} h-9 w-full md:w-40`} data-testid={testid}/>
);

export const Pager = ({ page, pageSize, total, onPage, testid = "pager" }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between mt-4 text-xs text-slate-400" data-testid={testid}>
      <span data-testid={`${testid}-info`}>{total} record{total === 1 ? "" : "s"} · page {page} of {pages}</span>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="border-slate-700 bg-transparent text-slate-300 h-8" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}><ChevronLeft size={14}/></Button>
        <Button size="sm" variant="outline" className="border-slate-700 bg-transparent text-slate-300 h-8" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}><ChevronRight size={14}/></Button>
      </div>
    </div>
  );
};

export const MfaField = ({ value, onChange, testid = "mfa-code-input" }) => (
  <div>
    <label className="text-xs font-semibold text-slate-400">Google Authenticator code</label>
    <Input inputMode="numeric" maxLength={6} value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
      placeholder="6-digit code" className={`${DARK_INPUT} font-mono tracking-[0.4em] mt-1`} data-testid={testid}/>
  </div>
);

export const Stat = ({ label, value, tone = "text-orange-400", testid }) => (
  <div className="admin-card p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
    <div className={`font-mono text-2xl font-bold mt-1 ${tone}`}>{value}</div>
  </div>
);

export const useDebounced = (value, ms = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

export const useLatest = () => {
  const seq = useRef(0);
  return () => { const id = ++seq.current; return () => id === seq.current; };
};
